import { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import AchievementBadge from './AchievementBadge';
import { getAchievementDescription, type ParsedAchievement } from '../achievementHelpers';

interface BadgeGroup {
  parsed: ParsedAchievement;
  count: number;
}

interface AchievementBadgeRowProps {
  badgeGroups: BadgeGroup[];
  // 'center' for profile page, 'left' (default) for leaderboard card
  justify?: 'center' | 'flex-start';
}

const DESCRIPTION_TEXT_COLOR = 'rgba(225,190,231,0.75)';
const DESCRIPTION_BORDER_COLOR = 'rgba(206,147,216,0.15)';

export default function AchievementBadgeRow({ badgeGroups, justify = 'flex-start' }: AchievementBadgeRowProps) {
  // null = nothing selected; achievementType string = selected badge
  const [selectedType, setSelectedType] = useState<string | null>(null);

  if (badgeGroups.length === 0) return null;

  const selectedGroup = badgeGroups.find(g => g.parsed.achievementType === selectedType) ?? null;

  const handleBadgeClick = (achievementType: string) => {
    // Tap same badge again to collapse
    setSelectedType(prev => prev === achievementType ? null : achievementType);
  };

  return (
    <Box>
      {/* Badge row — wraps naturally on narrow screens */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: justify }}>
        {badgeGroups.map(({ parsed, count }) => (
          <AchievementBadge
            key={parsed.achievementType}
            parsed={parsed}
            count={count}
            isSelected={selectedType === parsed.achievementType}
            onClick={() => handleBadgeClick(parsed.achievementType)}
          />
        ))}
      </Box>

      {/* Description panel — slides open below the badge row when a badge is selected */}
      <Collapse in={selectedGroup !== null} unmountOnExit>
        <Box sx={{
          mt: 1,
          pt: 1,
          borderTop: `1px solid ${DESCRIPTION_BORDER_COLOR}`,
        }}>
          {selectedGroup && (
            <Typography sx={{
              fontSize: 13,
              color: DESCRIPTION_TEXT_COLOR,
              lineHeight: 1.5,
              // Allow long descriptions to wrap cleanly rather than overflow
              wordBreak: 'break-word',
            }}>
              {selectedGroup.parsed.icon}{' '}
              {getAchievementDescription(selectedGroup.parsed)}
              {selectedGroup.count > 1 && (
                <Box component="span" sx={{ opacity: 0.6 }}>
                  {' '}(×{selectedGroup.count})
                </Box>
              )}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}